import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, MessageCircle, Calendar, Globe, Sparkles, ArrowRight,
  Mic, BookOpen, Award, MapPin, Clock, Search, Heart, Coffee
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const HUBS = [
  {
    id: "leaders-circle",
    icon: Users,
    color: "#3B82F6",
    title: "Leaders' Circle",
    desc: "Closed-door peer group for CEOs and founders of certified organisations. Quarterly virtual sessions and an annual in-person summit.",
    members: "180+ leaders",
    tag: "Invite only",
  },
  {
    id: "practitioners-guild",
    icon: BookOpen,
    color: "#10B981",
    title: "Practitioners Guild",
    desc: "For CHROs, ESG leads, and culture champions implementing the Six-Pillar Framework day-to-day. Monthly playbook drops + open Q&A.",
    members: "640+ practitioners",
    tag: "Open to all certified orgs",
  },
  {
    id: "partner-pavilion",
    icon: Globe,
    color: "#FDB813",
    title: "Partner Pavilion",
    desc: "CNESS partners across consulting, media, education and technology come together to share leads, co-author content, and deliver workshops.",
    members: "200+ partners",
    tag: "For Partner-Program members",
  },
  {
    id: "founders-table",
    icon: Coffee,
    color: "#EC4899",
    title: "Founders' Table",
    desc: "An informal monthly gathering for early-stage founders exploring conscious enterprise. No slides, no pitches — just real conversations.",
    members: "300+ founders",
    tag: "Free, public",
  },
];

const EVENTS = [
  {
    title: "CNESS Conscious Enterprise Summit 2026",
    date: "October 14–15, 2026",
    location: "Bengaluru, India · Hybrid",
    desc: "Two days. 40 speakers. The flagship gathering of the conscious-enterprise movement — keynotes, breakouts, and the annual Luminary Awards.",
    cta: "Reserve Seat",
    href: "/contact",
    accent: "#FDB813",
  },
  {
    title: "Six-Pillar Practitioners Workshop (Monthly)",
    date: "First Thursday · Every Month",
    location: "Online · Free",
    desc: "Live 75-minute workshop where the CNESS team and certified practitioners walk through one pillar in depth. Bring questions, leave with templates.",
    cta: "Register",
    href: "/contact",
    accent: "#10B981",
  },
  {
    title: "Founders' Table — January Edition",
    date: "January 28, 2026 · 7 PM IST",
    location: "Online · 90 min",
    desc: "Casual roundtable for early-stage founders building values-led companies. Hosted by the CNESS founder and one Luminary-tier guest.",
    cta: "Save Spot",
    href: "/enquiries",
    accent: "#EC4899",
  },
];

const RESOURCES = [
  { icon: BookOpen, title: "Conscious Enterprise Playbook", desc: "28-page guide on installing the Six-Pillar Framework." },
  { icon: Mic, title: "Champion Voices Podcast", desc: "Bi-weekly interviews with certified leaders sharing what changed." },
  { icon: Award, title: "Pillar Templates Library", desc: "Battle-tested policy, scorecard and review templates." },
];

const CommunityPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const filteredHubs = HUBS.filter(
    (h) =>
      !search ||
      h.title.toLowerCase().includes(search.toLowerCase()) ||
      h.desc.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#FFFBEB] via-white to-[#F8FAFC]" data-testid="community-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Heart className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">CNESS Community Hub</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3A5F] mb-6 leading-[1.1]">
              The home for <span className="text-[#FDB813]">conscious leaders</span>,<br />
              practitioners and partners.
            </h1>
            <p className="text-lg text-gray-600">
              Certification gets you the badge. The community is where the badge keeps compounding —
              through peers, playbooks, and live events that turn conscious enterprise into a daily practice.
            </p>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-10 bg-white border-y border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-5xl mx-auto text-center">
            {[
              { v: "1,300+", l: "Active members" },
              { v: "30+", l: "Countries" },
              { v: "12", l: "Live events / yr" },
              { v: "60+", l: "Resource templates" },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-3xl font-bold text-[#FDB813] mb-1">{s.v}</div>
                <div className="text-sm text-gray-500">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hubs */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="community-hubs">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-3">Find your circle</h2>
            <p className="text-gray-600">Four active hubs — pick the one that matches where you sit in the ecosystem.</p>
          </div>

          {/* Search */}
          <div className="relative max-w-xl mx-auto mb-10">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              data-testid="community-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search hubs..."
              className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:border-[#FDB813] focus:ring-2 focus:ring-[#FDB813]/20 bg-white"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {filteredHubs.map((hub) => (
              <div
                key={hub.id}
                className="bg-white rounded-3xl p-7 border border-gray-100 hover:shadow-xl hover:border-[#FDB813]/30 hover:-translate-y-1 transition-all"
                data-testid={`community-hub-${hub.id}`}
              >
                <div className="flex items-start gap-4 mb-5">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${hub.color}15` }}
                  >
                    <hub.icon className="w-7 h-7" style={{ color: hub.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#1E3A5F] mb-1">{hub.title}</h3>
                    <p className="text-xs font-semibold text-gray-500">{hub.members} · <span style={{ color: hub.color }}>{hub.tag}</span></p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed mb-5">{hub.desc}</p>
                <Button
                  onClick={() => navigate("/contact")}
                  className="w-full bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl"
                >
                  Join {hub.title} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ))}
            {filteredHubs.length === 0 && (
              <p className="col-span-2 text-center text-gray-500 py-8">No hubs match "{search}". Try clearing the search.</p>
            )}
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="py-16 bg-white" data-testid="community-events">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-3">Upcoming events</h2>
            <p className="text-gray-600">From flagship summits to monthly working sessions — there's always something on the calendar.</p>
          </div>
          <div className="space-y-4 max-w-5xl mx-auto">
            {EVENTS.map((ev, i) => (
              <div
                key={i}
                className="grid sm:grid-cols-12 gap-6 items-center bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-lg transition"
                data-testid={`community-event-${i}`}
              >
                <div className="sm:col-span-3 flex sm:flex-col items-center sm:items-start gap-3">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${ev.accent}15` }}
                  >
                    <Calendar className="w-7 h-7" style={{ color: ev.accent }} />
                  </div>
                  <div className="text-sm">
                    <p className="font-semibold text-[#1E3A5F]">{ev.date}</p>
                    <p className="text-xs text-gray-500 flex items-center"><MapPin className="w-3 h-3 mr-1" />{ev.location}</p>
                  </div>
                </div>
                <div className="sm:col-span-7">
                  <h3 className="text-lg font-bold text-[#1E3A5F] mb-2">{ev.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{ev.desc}</p>
                </div>
                <div className="sm:col-span-2 flex sm:justify-end">
                  <Button
                    onClick={() => navigate(ev.href)}
                    className="bg-[#1E3A5F] hover:bg-[#152a45] text-white rounded-xl px-5 py-3 text-sm whitespace-nowrap"
                  >
                    {ev.cta}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Conversations from the Community */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="community-conversations">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-3">Latest from the community</h2>
            <p className="text-gray-600">Real questions, real conversations from members across the network.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {[
              {
                topic: "How are you measuring 'culture' as a hard KPI?",
                hub: "Practitioners Guild",
                replies: 23,
                last: "2 hours ago",
                author: "CHRO, Mid-cap Manufacturing",
                accent: "#10B981",
              },
              {
                topic: "Vendor scorecards that actually surface conscious-supplier behaviour",
                hub: "Leaders' Circle",
                replies: 12,
                last: "Yesterday",
                author: "Group CEO, FMCG",
                accent: "#3B82F6",
              },
              {
                topic: "Templates for the Brand Vision pillar evidence pack",
                hub: "Practitioners Guild",
                replies: 18,
                last: "3 days ago",
                author: "Head of Brand, Series-B SaaS",
                accent: "#10B981",
              },
              {
                topic: "Co-hosting a Founders' Table session in Bengaluru — who's interested?",
                hub: "Founders' Table",
                replies: 31,
                last: "Last week",
                author: "Founder, Climate-tech",
                accent: "#EC4899",
              },
            ].map((t, i) => (
              <div
                key={i}
                onClick={() => navigate("/contact")}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer"
                data-testid={`community-thread-${i}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full"
                    style={{ backgroundColor: `${t.accent}15`, color: t.accent }}
                  >
                    {t.hub}
                  </span>
                  <span className="text-xs text-gray-400">· {t.last}</span>
                </div>
                <h3 className="text-base font-bold text-[#1E3A5F] mb-3 leading-snug">{t.topic}</h3>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{t.author}</span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-4 h-4" />
                    {t.replies} replies
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button
              onClick={() => navigate("/contact")}
              variant="outline"
              data-testid="community-join-conversations"
              className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white rounded-xl px-8 py-3"
            >
              Request Access to the Conversations <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#FEF3C7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Sparkles className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Want in?</h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-8">
            Drop us a note about who you are and which hub feels right — we'll reach out within one business day.
          </p>
          <Button
            onClick={() => navigate("/contact")}
            data-testid="community-cta"
            className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-6 rounded-xl"
          >
            Request to Join <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default CommunityPage;
